import type {
  BrainstormStructuredData,
  Client,
  DraftTask,
  MapLocationsData,
  Recurrence,
  TaskComment,
  TaskCommentAuthor,
  TaskDetail,
} from '@frontend/api-types';
import type { StrictExclude, StrictOmit } from '@frontend/duck-tape';
import { P, concat, dayjs, match, replaceAll } from '@frontend/duck-tape';
import { getInitials } from '../../client/utils';

const formatHtml = (text: string) => replaceAll(text, '\n', '<br/>');

type CommentConfig = {
  labelClassName: string;
  textClassName?: string;
};

export const getInitialComment = (
  task: DraftTask | Recurrence | TaskDetail,
  client: Client | undefined,
  config?: CommentConfig,
) => {
  const { createdAt, description, proceedDecision } = task;

  const descriptionHtml = `<div class="${config?.textClassName}">${formatHtml(description)}</div>`;

  const proceedDecisionHtml =
    `<br/><div class="${config?.labelClassName}">How should we proceed?</div>` +
    `<div class="${config?.textClassName}">${formatHtml(proceedDecision)}</div>`;

  const initialQuestionsHtml =
    'aiQuestionnaire' in task && task.aiQuestionnaire
      ? task.aiQuestionnaire.responses.reduce((agg: string, IQ) => {
          const formattedIQ = match(IQ)
            .with(
              { type: 'multi_choice' },
              (IQ) =>
                `<div class="${config?.labelClassName}">${formatHtml(IQ.question)}</div><div class="${config?.textClassName}">${formatHtml(IQ.answer.join(', '))}</div>`,
            )
            .with(
              { type: 'text' },
              (IQ) =>
                `<div class="${config?.labelClassName}">${formatHtml(IQ.question)}</div><div class="${config?.textClassName}">${formatHtml(IQ.answer ?? 'n/a')}</div>`,
            )
            .with(
              { type: 'secure_note' },
              (IQ) =>
                `<div class="${config?.labelClassName}">${formatHtml(IQ.secureNoteTitle)}</div><div class="${config?.textClassName}">${IQ.secureNoteId ? 'Provided ✅' : 'Skipped ❌'}</div>`,
            )
            .exhaustive();

          return `${agg}<br/>${formattedIQ}`;
        }, '')
      : '';

  const body = concat(descriptionHtml, proceedDecisionHtml, initialQuestionsHtml).join('');

  const { firstName, lastName, nickname } = client ?? {};
  const author: TaskCommentAuthor = { firstName, isUser: true, lastName, nickname, type: 'client', zendeskId: '' };

  return {
    attachments: [],
    author,
    body,
    createdAt,
    id: '',
    isUser: true,
    secureNote: null,
    sheets: [],
  } as TaskComment;
};

export const getCommentProps = ({
  currentComment,
  previousComment,
}: {
  currentComment: TaskComment;
  previousComment: MaybeUndefined<TaskComment>;
}) => {
  // Show time if it's the first message or it's been at least an hour since the previous message
  const getIsTimeVisible = () => {
    if (!previousComment) return true;
    const previousMessageDate = dayjs(previousComment.createdAt);
    const currentMessageDate = dayjs(currentComment.createdAt);
    return currentMessageDate.diff(previousMessageDate, 'hours') >= 1;
  };

  const isTimeVisible = getIsTimeVisible();

  // Show author if it's not a system bot message and the time is visible or the previous author was different
  const isAuthorVisible = (() => {
    if (currentComment.author.type === 'system_bot') return false;
    return isTimeVisible || !previousComment || previousComment.author.type !== currentComment.author.type;
  })();

  return { isAuthorVisible, isTimeVisible };
};

export const isClientResponseAuthorType = (type: MaybeUndefined<TaskCommentAuthor['type']>) =>
  type === 'client' || type === 'super_agent';

// We don't render author info for system bot messages, those usually turn into whispers
export type TaskChatAuthorType = StrictExclude<TaskCommentAuthor['type'], 'system_bot'> | 'automated_message';

export type TaskChatAuthorInfo = {
  initials: string;
  // Usually first name + last name
  label: string;
  type: TaskChatAuthorType;
};

export const getTaskChatMessageAuthorInfo = ({
  firstName = '',
  isUser,
  lastName = '',
  nickname = '',
  type,
}: StrictOmit<TaskCommentAuthor, 'type'> & { type: TaskChatAuthorType }): TaskChatAuthorInfo => {
  const preferredName = nickname || firstName;
  const initials = getInitials({ firstName, lastName, nickname });

  const label: string = (() => {
    const duckbotText = 'Duckbot';
    if (isUser) return 'You';
    return match(type)
      .with(P.union('agent', 'ai_agent'), () => 'Copilot')
      .with(P.union('client', 'super_agent'), () => preferredName)
      .with(P.union('ai_bot', 'automated_message'), () => duckbotText)
      .otherwise(() => preferredName || 'Unknown');
  })();
  return { initials, label, type: type === 'ai_bot' ? 'automated_message' : type };
};

/**
 * Groups "system_bot" comments (whispers) into arrays based on the day they were created.
 * Non-whisper comments are kept as individual elements.
 *
 * @param comments - An array of TaskComment objects to process.
 * @returns An array where whispers are grouped by day into arrays,
 *          and non-whisper comments are left as individual elements.
 */
export const groupWhispers = (comments: TaskComment[]) => {
  return comments.reduce<(TaskComment | TaskComment[])[]>((acc, comment) => {
    const currentDate = new Date(comment.createdAt).toDateString();
    const lastItem = acc[acc.length - 1];

    // Check if the comment is a whisper (system_bot)
    if (comment.author?.type === 'system_bot') {
      // If the last item is an array (a group), check the date to append to the same group
      if (Array.isArray(lastItem) && lastItem.length > 0) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const lastGroupDate = new Date(lastItem[0].createdAt).toDateString();
        if (currentDate === lastGroupDate) {
          return [
            ...acc.slice(0, -1), // Keep all but the last item
            [...lastItem, comment], // Append to the existing group
          ];
        }
      }

      // Start a new group for whispers
      return [...acc, [comment]];
    } else {
      // Push non-whisper comments as individual elements
      return [...acc, comment];
    }
  }, []);
};

const normalizeMapPayload = (parsed: Record<string, unknown>): MapLocationsData => ({
  locations: ((parsed['locations'] as Array<Record<string, unknown>>) ?? []).map((loc) => ({
    label: (loc['label'] as string) ?? '',
    lat: (loc['latitude'] as number) ?? (loc['lat'] as number) ?? null,
    lng: (loc['longitude'] as number) ?? (loc['lng'] as number) ?? null,
    placeId: (loc['place_id'] as string) ?? (loc['placeId'] as string) ?? '',
  })),
  message: (parsed['summary'] as string) ?? (parsed['message'] as string) ?? '',
  type: 'map_locations',
});

/**
 * Tries to parse a comment body as JSON and extract structured data.
 * Supports 'clarifying_questions' → questions field and 'map_locations' → mapLocations field.
 */
const tryParseStructuredBody = (
  body: string,
): { body: string; mapLocations?: MapLocationsData; questions?: BrainstormStructuredData } => {
  if (!body.startsWith('{')) return { body };

  try {
    const parsed = JSON.parse(body) as BrainstormStructuredData | MapLocationsData | Record<string, unknown>;
    if ((parsed as BrainstormStructuredData).type === 'clarifying_questions') {
      return { body: '', questions: parsed as BrainstormStructuredData };
    }
    if ((parsed as MapLocationsData).type === 'map_locations') {
      return { body: (parsed as MapLocationsData).message, mapLocations: parsed as MapLocationsData };
    }
    if ((parsed as Record<string, unknown>)['type'] === 'map') {
      const normalized = normalizeMapPayload(parsed as Record<string, unknown>);
      return { body: normalized.message, mapLocations: normalized };
    }
  } catch {
    // Not valid JSON, return body as-is
  }

  return { body };
};

/**
 * Transforms API comments by extracting structured data from body JSON.
 * This allows storing questions/map data in the body field while maintaining
 * backwards compatibility with the typed fields on TaskComment.
 */
export const transformCommentQuestions = (comments: TaskComment[]): TaskComment[] =>
  comments.map((comment) => {
    if (comment.questions || comment.mapLocations) return comment;

    const { body, mapLocations, questions } = tryParseStructuredBody(comment.body);
    if (questions) return { ...comment, body, questions };
    if (mapLocations) return { ...comment, body, mapLocations };
    return comment;
  });
